import { useDebouncedCallback } from 'use-debounce';
import React, { Suspense, useState } from 'react';
import {
    Box,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Link,
    SimpleGrid,
    Skeleton,
    VStack,
    Image,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaIcon, NonIdealState } from '@acp/core-ui';
import { faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { brandIcons } from '@acp/core-ui/assets';
import { usePortfolioProperties } from '../../portfolio';
import { CardLayout } from './card-layout';

const PropertyRow = React.lazy(
    () => import('../../portfolio').then((module) => ({ default: module.PropertyRow }))
);

function ResultSkeleton({
    pageSize,
}: {
    pageSize: number;
}) {
    return (
        <SimpleGrid columns={[1, 1, 2, 2, 1]} spacing={4} w="full">
            {new Array(pageSize).fill(0).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
                <Skeleton key={idx}>
                    <Box w="full" h="100px" />
                </Skeleton>
            ))}
        </SimpleGrid>
    );
}

export function SearchPropertyCard({
    pageSize,
}: {
    pageSize: number;
}) {
    const [query, setQuery] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const { data, isLoading } = usePortfolioProperties(
        {
            query,
            services: [],
            facets: [],
            serviceExclusivity: null,
            propertyManager: null,
            newAccessIssues: false,
        },
        1,
        pageSize,
        {
            isEnabled: query !== '',
        }
    );

    const debouncedChangeQuery = useDebouncedCallback(
        (value) => setQuery(value),
        300
    );

    const handleClearSearch = () => {
        setSearchValue('');
        setQuery('');
    };

    return (
        <CardLayout bgColor="white">
            <CardLayout.Title.Heading heading="Find a property in your portfolio" />
            <CardLayout.Body gap={4}>
                <InputGroup size="lg">
                    <InputLeftElement w="3rem">
                        <FaIcon icon={faSearch} color="gray.500" />
                    </InputLeftElement>
                    <Input
                        pl="3rem"
                        pr="3rem"
                        type="text"
                        placeholder="Search your portfolio"
                        value={searchValue}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeQuery(e.target.value);
                        }}
                    />

                    {
                        searchValue !== '' && (
                            <InputRightElement>
                                <IconButton
                                    w={1}
                                    h={1}
                                    aria-label="Clear search"
                                    icon={<FaIcon color="black" icon={faTimes} />}
                                    variant="ghost"
                                    colorScheme="blackAlpha"
                                    _hover={{
                                        backgroundColor: 'transparent',
                                    }}
                                    onClick={() => handleClearSearch()}
                                />
                            </InputRightElement>
                        )
                    }

                </InputGroup>
                {
                    isLoading && (
                        <ResultSkeleton pageSize={pageSize} />
                    )
                }
                {
                    data !== undefined && (
                        <VStack spacing={4} w="full">
                            <Suspense fallback={<ResultSkeleton pageSize={pageSize} />}>
                                <SimpleGrid columns={[1, 1, 2, 2, 1]} spacing={4} w="full">
                                    {data?.items.map((item) => (
                                        <PropertyRow
                                            key={item.propertyId}
                                            property={item}
                                        />
                                    ))}
                                </SimpleGrid>
                            </Suspense>
                            {
                                data !== undefined && data.total > pageSize && (
                                    <Link as={RouterLink} to={`/portfolio?query=${query}`} alignSelf="start">
                                        View all
                                        {' '}
                                        {data.total}
                                        {' '}
                                        results
                                    </Link>
                                )
                            }
                        </VStack>
                    )
                }
                {
                    data !== undefined && data.total === 0 && (
                        <NonIdealState
                            iconComponent={<Image src={brandIcons.noResultProperty} />}
                            title="No properties found"
                            description="We could not find any properties that matched your search criteria"
                        />
                    )
                }
            </CardLayout.Body>
        </CardLayout>
    );
}
